import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import api from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Users, Briefcase, TrendingUp, Activity, DollarSign } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ADMIN_EMAIL = 'wilhelm.wagner.consulting@gmail.com';
const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'];

export default function Admin() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('analytics'); // 'analytics' or 'beta-manager'
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (user && user.email === ADMIN_EMAIL) {
      if (activeTab === 'analytics') {
        fetchAnalytics();
      } else if (activeTab === 'beta-manager') {
        fetchUsers();
      }
    }
  }, [user, activeTab]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users', { params: { search, limit: 50 } });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage('❌ Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    fetchUsers();
  };

  const handleGrantLifetime = async (userId, email, plan = 'pro') => {
    if (!window.confirm(`Grant lifetime ${plan.toUpperCase()} access to ${email}?`)) {
      return;
    }

    try {
      const response = await api.post(`/admin/users/${userId}/grant-lifetime`, { plan });
      setMessage(`✅ ${response.data.message}`);
      fetchUsers(); // Refresh list
    } catch (error) {
      console.error('Grant lifetime failed:', error);
      setMessage(`❌ Failed: ${error.response?.data?.detail || 'Unknown error'}`);
    }
  };

  const handleRevokeLifetime = async (userId, email) => {
    if (!window.confirm(`Revoke lifetime access for ${email}? User will revert to free plan.`)) {
      return;
    }

    try {
      const response = await api.delete(`/admin/users/${userId}/revoke-lifetime`);
      setMessage(`✅ ${response.data.message}`);
      fetchUsers(); // Refresh list
    } catch (error) {
      console.error('Revoke lifetime failed:', error);
      setMessage(`❌ Failed: ${error.response?.data?.detail || 'Unknown error'}`);
    }
  };

  // Check if user is admin
  if (!user || user.email !== ADMIN_EMAIL) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
        <p className="text-lg text-gray-600">Platform management and analytics</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('analytics')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'analytics'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📊 Analytics
          </button>
          <button
            onClick={() => setActiveTab('beta-manager')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'beta-manager'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🎁 Beta Tester Manager
          </button>
        </nav>
      </div>

      {/* Analytics Tab */}
      {activeTab === 'analytics' && analytics && (
        <div className="space-y-8">

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalUsers}</div>
            <p className="text-xs text-gray-500 mt-1">Registered accounts</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
            <Briefcase className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalProjects}</div>
            <p className="text-xs text-gray-500 mt-1">Active projects</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Customers</CardTitle>
            <Users className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalCustomers}</div>
            <p className="text-xs text-gray-500 mt-1">Customer records</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Invoices</CardTitle>
            <DollarSign className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalInvoices}</div>
            <p className="text-xs text-gray-500 mt-1">Invoices created</p>
          </CardContent>
        </Card>
      </div>

      {/* Plan Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Plan Distribution</CardTitle>
            <CardDescription>Users by subscription plan</CardDescription>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={analytics.planDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => `${name}: ${value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {analytics.planDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>User Activity</CardTitle>
            <CardDescription>Activity metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Time Entries</span>
                <span className="text-lg font-semibold">{analytics.totalTimeEntries}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Hours Tracked</span>
                <span className="text-lg font-semibold">{analytics.totalHoursTracked?.toFixed(1) || 0}h</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Active Users (with data)</span>
                <span className="text-lg font-semibold">{analytics.activeUsers}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Tour Completion Rate</span>
                <span className="text-lg font-semibold">
                  {analytics.tourCompletionRate ? `${(analytics.tourCompletionRate * 100).toFixed(1)}%` : '0%'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Registrations Over Time */}
      {analytics.userRegistrationsOverTime && analytics.userRegistrationsOverTime.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>User Registrations</CardTitle>
            <CardDescription>New user signups over time</CardDescription>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.userRegistrationsOverTime}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="#10b981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Feature Usage by Plan */}
      {analytics.featureUsage && (
        <Card>
          <CardHeader>
            <CardTitle>Feature Usage by Plan</CardTitle>
            <CardDescription>Average resources per plan</CardDescription>
          </CardHeader>
          <CardContent className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.featureUsage}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="plan" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="avgCustomers" fill="#10b981" name="Avg Customers" />
                <Bar dataKey="avgProjects" fill="#3b82f6" name="Avg Projects" />
                <Bar dataKey="avgInvoices" fill="#f59e0b" name="Avg Invoices" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      </div>
      )}

      {/* Beta Manager Tab */}
      {activeTab === 'beta-manager' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>🎁 Beta Tester Management</CardTitle>
              <CardDescription>Grant or revoke lifetime access to beta testers</CardDescription>
            </CardHeader>
            <CardContent>
              {message && (
                <div className={`mb-4 p-4 rounded-lg ${message.includes('❌') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                  {message}
                </div>
              )}

              {/* Search */}
              <form onSubmit={handleSearch} className="flex gap-4 mb-6">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by email or name..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-50"
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </form>

              {/* User List */}
              {!loading && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Current Plan</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Verified</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {users.map((u) => (
                        <tr key={u.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{u.email}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{u.name || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                              u.plan === 'pro' ? 'bg-purple-100 text-purple-800' :
                              u.plan === 'basic' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {u.plan.toUpperCase()}
                              {u.stripe_subscription_id?.includes('lifetime_beta') && ' 🎁'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {u.email_verified ? (
                              <span className="text-green-600">✓ Verified</span>
                            ) : (
                              <span className="text-gray-400">Not verified</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              {u.stripe_subscription_id?.includes('lifetime_beta') ? (
                                <button
                                  onClick={() => handleRevokeLifetime(u.id, u.email)}
                                  className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold hover:bg-red-200"
                                >
                                  Revoke Lifetime
                                </button>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleGrantLifetime(u.id, u.email, 'basic')}
                                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold hover:bg-blue-200"
                                  >
                                    Grant Basic
                                  </button>
                                  <button
                                    onClick={() => handleGrantLifetime(u.id, u.email, 'pro')}
                                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold hover:bg-purple-200"
                                  >
                                    Grant Pro
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {users.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      No users found. Try searching or loading all users.
                    </div>
                  )}

                  <div className="mt-6 text-sm text-gray-600">
                    <p><strong>Total users:</strong> {users.length}</p>
                    <p className="mt-2">
                      <strong>Legend:</strong> 🎁 = Beta Tester with Lifetime Access
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
